import React, { useState } from 'react'
import { FiSend, FiLoader, FiMusic } from 'react-icons/fi'

interface PlaylistOptions {
  trackCount: number
}

interface MoodFormProps {
  onSubmit: (mood: string, options?: Partial<PlaylistOptions>) => void
  isGenerating?: boolean
  currentMood?: string | null
  defaultOptions?: PlaylistOptions
}

// Common moods for quick selection
const QUICK_MOODS = [
  { label: '😊 Happy', value: 'happy' },
  { label: '😢 Sad', value: 'sad' },
  { label: '⚡ Energetic', value: 'energetic' },
  { label: '😌 Relaxed', value: 'relaxed' },
  { label: '🎯 Focused', value: 'focused' },
  { label: '😴 Sleepy', value: 'sleepy' },
  { label: '🌞 Sunny', value: 'sunny' },
  { label: '💖 Romantic', value: 'romantic' }
]

const MoodForm: React.FC<MoodFormProps> = ({ 
  onSubmit, 
  isGenerating = false,
  currentMood = null,
  defaultOptions = { trackCount: 10 }
}) => {
  const [mood, setMood] = useState(currentMood || '')
  const [isLoading, setIsLoading] = useState(false)
  const [trackCount, setTrackCount] = useState(defaultOptions.trackCount)
  const [advanced, setAdvanced] = useState(false)
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!mood.trim()) return
    
    setIsLoading(true)
    
    try {
      // Create options object with track count
      const options: Partial<PlaylistOptions> = { trackCount };
      
      onSubmit(mood.trim(), options)
    } catch (error) {
      console.error('Error submitting mood:', error)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="bg-spotify-light rounded-lg p-4 md:p-6 shadow-lg">
      <div className="mb-4 md:mb-6">
        <h2 className="text-xl font-bold mb-2">Tell us your mood</h2>
        <p className="text-spotify-text-gray">
          Describe how you're feeling and we'll create a playlist that matches your mood
        </p>
      </div>

      {/* Quick Mood Selection */}
      <div className="mb-4 md:mb-6">
        <label className="block mb-2 font-medium">Quick moods</label>
        <div className="flex flex-wrap gap-2">
          {QUICK_MOODS.map(({ label, value }) => (
            <button
              key={value}
              type="button"
              onClick={() => setMood(value)}
              className={`px-3 py-1.5 rounded-full text-sm ${
                mood === value 
                  ? 'bg-spotify-green text-white' 
                  : 'bg-spotify-input text-spotify-text-gray hover:bg-spotify-border transition-colors'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      
      <form onSubmit={handleSubmit}>
        <div className="mb-4 md:mb-6">
          <label htmlFor="mood" className="block mb-2 font-medium">
            Your mood
          </label>
          <div className="relative">
            <textarea
              id="mood"
              value={mood}
              onChange={(e) => setMood(e.target.value)}
              placeholder="I'm feeling..."
              className="w-full px-4 py-3 rounded-lg border border-spotify-border bg-spotify-input text-spotify-text-light focus:outline-none focus:border-spotify-green resize-none min-h-[80px] md:min-h-[120px]"
              required
            />
          </div>
          <div className="text-right text-sm text-spotify-text-gray mt-1">
            <span>{mood.length}</span> characters
          </div>
        </div>
        
        <div className="mb-4 md:mb-6">
          <div className="flex items-center mb-3">
            <h3 className="font-medium">Number of songs</h3>
          </div>
          
          <div>
            <label className="block mb-2 text-sm font-medium text-spotify-text-gray">
              Number of tracks: <span className="font-bold">{trackCount}</span>
            </label>
            <input
              type="range"
              min="5"
              max="30"
              step="1"
              value={trackCount}
              onChange={(e) => setTrackCount(parseInt(e.target.value))}
              className="w-full h-2 bg-spotify-dark-tint rounded-lg appearance-none cursor-pointer accent-spotify-green"
            />
            <div className="flex justify-between text-xs text-spotify-text-gray mt-1">
              <span>5</span>
              <span>30</span>
            </div>
          </div>
        </div>
        
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={isGenerating || !mood.trim()}
            className="w-full md:w-auto px-6 py-3 bg-spotify-green text-white font-medium rounded-full hover:bg-opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {isGenerating ? (
              <>
                <FiLoader className="animate-spin mr-2" />
                <span>Creating Playlist...</span>
              </>
            ) : (
              <>
                <FiSend className="mr-2" />
                <span>Create Playlist</span>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  )
}

export default MoodForm 